import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  FiActivity,
  FiArrowRight,
  FiBookOpen,
  FiBox,
  FiBriefcase,
  FiCheck,
  FiChevronDown,
  FiCpu,
  FiDollarSign,
  FiExternalLink,
  FiGrid,
  FiHelpCircle,
  FiLayers,
  FiMail,
  FiSearch,
  FiShield,
  FiUsers,
  FiX,
  FiZap,
} from "react-icons/fi";
import "./Sidebar.css";

/**
 * Drop-in replacement for the old mobile sidebar.
 * Same public API: <EthosinoMobileSidebar toggled={toggled} onClose={onClose} />
 * Uses the existing react-router-dom + framer-motion dependencies.
 */
const NAV_GROUPS = [
  {
    id: "platform",
    number: "01",
    name: "Platform",
    summary: "Systems behind the experience",
    icon: FiCpu,
    hue: "cyan",
    items: [
      { title: "Provably Fair Engine", note: "Outcome verification", to: "/provably-fair-engine", icon: FiShield },
      { title: "Public Docs", note: "Technical reference", to: "/docs", icon: FiBookOpen },
      { title: "Multi-Tenant Architecture", note: "Operator infrastructure", to: "/info/multi-tenant/infrastructure", icon: FiLayers },
      { title: "Risk & Exposure", note: "Protect the operation", to: "/risk-management", icon: FiActivity },
      { title: "Table Game Engine", note: "Integrated table experiences", to: "/table-games/engines", icon: FiGrid },
      { title: "Originals Framework", note: "First-party game studio", to: "/originals-custom", icon: FiBox },
    ],
    more: { label: "Explore full architecture", to: "/company/architecture-walkthrough" },
  },
  {
    id: "deployments",
    number: "02",
    name: "Deployments",
    summary: "Choose an operator model",
    icon: FiZap,
    hue: "violet",
    items: [
      { title: "Startup Operators", note: "From audience to pilot", to: "/solutions/startup-operators", icon: FiZap },
      { title: "Sweepstakes Deployment", note: "Dual-currency operation", to: "/solutions/sweepstakes", icon: FiDollarSign },
      { title: "Pricing & Licensing", note: "Understand commercial terms", to: "/pricing", icon: FiDollarSign },
    ],
  },
  {
    id: "resources",
    number: "03",
    name: "Resources",
    summary: "Research before you decide",
    icon: FiBookOpen,
    hue: "blue",
    items: [
      { title: "Architecture Walkthrough", note: "Explore the connected system", to: "/company/architecture-walkthrough", icon: FiLayers },
      { title: "FAQ", note: "Answers for operator evaluation", to: "/general/frequently-asked-questions", icon: FiHelpCircle },
      { title: "Infrastructure", note: "Technical platform overview", to: "/info/multi-tenant/infrastructure", icon: FiCpu },
    ],
  },
  {
    id: "company",
    number: "04",
    name: "Company",
    summary: "People, purpose and contact",
    icon: FiUsers,
    hue: "pink",
    items: [
      { title: "About Ethosino", note: "Our mission and product", to: "/about", icon: FiUsers },
      { title: "Careers", note: "Build with Ethosino", to: "/company/careers", icon: FiBriefcase },
      { title: "Contact", note: "Start the conversation", to: "/book-demo", icon: FiMail },
    ],
  },
];

const BASE_EXPANDED = ["platform", "deployments"];
const EMPTY_CALLBACK = () => { };
const ease = [0.16, 1, 0.3, 1];

function RouteTile({ item, active, onSelect, index, reduced }) {
  const Icon = item.icon;
  return (
    <motion.li
      className="e3d-nav-li"
      initial={reduced ? false : { opacity: 0, y: 14, rotateX: -10, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, rotateX: 0, scale: 1 }}
      transition={{ duration: reduced ? 0 : 0.36, delay: reduced ? 0 : Math.min(index * 0.035, 0.24), ease }}
    >
      <motion.div
        className="e3d-tile-motion"
        whileHover={reduced ? undefined : { x: 5, rotateY: -3, z: 12 }}
        whileTap={reduced ? undefined : { scale: 0.985 }}
        transition={{ type: "spring", stiffness: 440, damping: 28 }}
      >
        <Link to={item.to} onClick={onSelect} className={`e3d-nav-tile${active ? " is-current" : ""}`} aria-current={active ? "page" : undefined}>
          <span className="e3d-nav-tile-icon"><Icon aria-hidden="true" /></span>
          <span className="e3d-nav-tile-copy">
            <strong>{item.title}</strong>
            <small>{item.note}</small>
          </span>
          <span className="e3d-nav-tile-arrow" aria-hidden="true">{active ? <FiCheck /> : <FiArrowRight />}</span>
        </Link>
      </motion.div>
    </motion.li>
  );
}

export default function EthosinoMobileSidebar({ toggled, onClose = EMPTY_CALLBACK }) {
  const location = useLocation();
  const reduced = useReducedMotion();
  const drawerRef = useRef(null);
  const returnFocusRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(BASE_EXPANDED);
  const [visibleHint, setVisibleHint] = useState(false);
  const normalized = search.trim().toLowerCase();

  const groups = useMemo(() => NAV_GROUPS.map((group) => ({
    ...group,
    filtered: normalized
      ? group.items.filter((item) => `${item.title} ${item.note} ${group.name} ${group.summary}`.toLowerCase().includes(normalized))
      : group.items,
  })).filter((group) => group.filtered.length > 0), [normalized]);
  const resultCount = useMemo(() => groups.reduce((sum, group) => sum + group.filtered.length, 0), [groups]);

  useEffect(() => {
    if (!toggled || typeof document === "undefined") return undefined;
    const body = document.body;
    const oldOverflow = body.style.overflow;
    returnFocusRef.current = document.activeElement;
    body.style.overflow = "hidden";

    // The drawer is mounted by AnimatePresence on this commit.
    const focusId = requestAnimationFrame(() => drawerRef.current?.focus());
    const onKeys = (event) => {
      if (event.key === "Escape") { event.preventDefault(); closeRef.current(); return; }
      if (event.key !== "Tab" || !drawerRef.current) return;
      const items = Array.from(drawerRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter((item) => item.getClientRects().length > 0);
      if (!items.length) { event.preventDefault(); drawerRef.current.focus(); return; }
      const first = items[0]; const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === drawerRef.current)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener("keydown", onKeys);
    return () => {
      cancelAnimationFrame(focusId);
      document.removeEventListener("keydown", onKeys);
      body.style.overflow = oldOverflow;
      returnFocusRef.current?.focus?.();
    };
  }, [toggled]);

  useEffect(() => {
    if (toggled) { setSearch(""); setExpanded(BASE_EXPANDED); }
  }, [toggled]);

  const toggleGroup = (id) => setExpanded((before) => before.includes(id) ? before.filter((v) => v !== id) : [...before, id]);
  const showAll = (group) => Boolean(normalized) || expanded.includes(group.id);
  const handleSelect = () => { setVisibleHint(false); onClose(); };

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence initial={false}>
      {toggled && (
        <motion.div className="e3d-portal" key="e3d-nav" initial="initial" animate="open" exit="exit">
          <motion.div
            className="e3d-backdrop"
            aria-hidden="true"
            variants={{ initial: { opacity: 0 }, open: { opacity: 1, transition: { duration: reduced ? 0 : 0.35 } }, exit: { opacity: 0, transition: { duration: reduced ? 0 : 0.25 } } }}
            onClick={onClose}
          />
          <div className="e3d-perspective">
            <motion.aside
              className="e3d-drawer"
              role="dialog"
              aria-modal="true"
              aria-labelledby="e3d-dialog-title"
              tabIndex={-1}
              ref={drawerRef}
              variants={{
                initial: reduced ? { opacity: 0 } : { opacity: 0, x: "-103%", rotateY: -17, scale: 0.94, filter: "blur(9px)" },
                open: { opacity: 1, x: 0, rotateY: 0, scale: 1, filter: "blur(0px)", transition: reduced ? { duration: 0 } : { type: "spring", stiffness: 230, damping: 29, mass: 1.04 } },
                exit: reduced ? { opacity: 0, transition: { duration: 0 } } : { opacity: 0, x: "-103%", rotateY: -10, scale: 0.96, filter: "blur(5px)", transition: { duration: 0.32, ease: [0.7, 0, 0.8, 0.2] } },
              }}
            >
              <div className="e3d-bg-mesh" aria-hidden="true" />
              <div className="e3d-bg-beam" aria-hidden="true" />
              <div className="e3d-edge-light" aria-hidden="true" />

              <header className="e3d-header">
                <Link to="/" onClick={handleSelect} className="e3d-brand" aria-label="Ethosino home">
                  <span className="e3d-brand-icon"><img src={require("./assets/images/logo.png")} alt="" /></span>
                  <span className="e3d-brand-copy"><strong>ETHOSINO</strong><small>OPERATOR PLATFORM</small></span>
                </Link>
                <motion.button type="button" className="e3d-close" onClick={onClose} aria-label="Close navigation" whileTap={{ scale: 0.92 }}>
                  <FiX aria-hidden="true" />
                </motion.button>
              </header>

              <div className="e3d-content-scroll">
                <motion.section className="e3d-showcase" initial={reduced ? false : { opacity: 0, y: 22, rotateX: 9 }} animate={{ opacity: 1, y: 0, rotateX: 0 }} transition={{ duration: reduced ? 0 : 0.62, delay: reduced ? 0 : 0.16, ease }}>
                  <div className="e3d-showcase-orbit" aria-hidden="true"><span /><span /><span /></div>
                  <div className="e3d-showcase-eyebrow"><span className="e3d-signal" /> THE ETHOSINO ECOSYSTEM <FiArrowRight /></div>
                  <h2 id="e3d-dialog-title">Explore the <em>platform.</em></h2>
                  <p>From the player experience to the systems operators run behind it.</p>
                  <a href="https://demo.ethosino.com" target="_blank" rel="noopener noreferrer" className="e3d-demo-link" onClick={handleSelect}>
                    <span className="e3d-demo-disc"><FiZap aria-hidden="true" /></span>
                    <span><strong>Launch interactive demo</strong><small>Explore the player-facing environment</small></span>
                    <FiExternalLink className="e3d-demo-arrow" aria-hidden="true" />
                  </a>
                </motion.section>

                <div className="e3d-directory-heading"><span>EXPLORE / DIRECTORY</span><span>{NAV_GROUPS.reduce((sum, group) => sum + group.items.length, 0)} DESTINATIONS</span></div>
                <label className="e3d-search">
                  <FiSearch aria-hidden="true" />
                  <input aria-label="Search navigation" type="search" placeholder="Search the platform..." value={search} onChange={(event) => setSearch(event.target.value)} />
                  {search ? <button type="button" onClick={() => setSearch("")} aria-label="Clear search"><FiX /></button> : <span className="e3d-search-hint">FIND</span>}
                </label>
                {normalized && <div className="e3d-results" role="status">{resultCount} matching destinations</div>}

                <nav className="e3d-nav" aria-label="Ethosino site navigation">
                  {groups.map((group) => {
                    const Icon = group.icon;
                    const isOpen = showAll(group);
                    return (
                      <section key={group.id} className={`e3d-group e3d-group--${group.hue}`}>
                        <button type="button" className="e3d-group-header" aria-expanded={isOpen} aria-controls={`e3d-group-${group.id}`} onClick={() => { if (!normalized) toggleGroup(group.id); }}>
                          <span className="e3d-group-number">{group.number}</span>
                          <span className="e3d-group-icon"><Icon aria-hidden="true" /></span>
                          <span className="e3d-group-copy"><strong>{group.name}</strong><small>{group.summary}</small></span>
                          <span className="e3d-group-count">{group.filtered.length}</span>
                          <motion.span className="e3d-group-chevron" animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: reduced ? 0 : 0.25 }}><FiChevronDown aria-hidden="true" /></motion.span>
                        </button>
                        <AnimatePresence initial={false}>
                          {isOpen && (
                            <motion.div id={`e3d-group-${group.id}`} className="e3d-group-body" initial={reduced ? false : { height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: reduced ? 0 : 0.34, ease }}>
                              <ul className="e3d-group-list">
                                {group.filtered.map((item, ii) => <RouteTile key={`${group.id}-${item.to}-${item.title}`} item={item} index={ii} reduced={reduced} active={location.pathname === item.to} onSelect={handleSelect} />)}
                              </ul>
                              {!normalized && group.more && (
                                <Link className="e3d-group-more" to={group.more.to} onClick={handleSelect}>{group.more.label}<FiArrowRight aria-hidden="true" /></Link>
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </section>
                    );
                  })}
                  {groups.length === 0 && (
                    <div className="e3d-empty"><FiSearch /><strong>No matching pages</strong><p>Try searching for games, pricing, sweepstakes, or support.</p><button type="button" onClick={() => setSearch("")}>Clear search</button></div>
                  )}
                </nav>
              </div>

              <footer className="e3d-footer">
                <Link to="/book-demo" className="e3d-footer-cta" onClick={handleSelect}>
                  <span className="e3d-footer-cta-icon"><FiArrowRight aria-hidden="true" /></span>
                  <span><small>LET'S TALK OPERATIONS</small><strong>Book a platform walkthrough</strong></span>
                  <FiArrowRight className="e3d-footer-cta-arrow" aria-hidden="true" />
                </Link>
                <div className="e3d-footer-meta"><span><i /> ETHOSINO</span><button type="button" onClick={() => setVisibleHint((v) => !v)} aria-expanded={visibleHint}>NAVIGATION INFO <FiChevronDown /></button></div>
                {visibleHint && <p className="e3d-footer-hint">Choose a section above or search for a page. Escape and the backdrop close this menu.</p>}
              </footer>
            </motion.aside>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
